/* global modForm Modalytics */
import {
	addServiceCodeToHeap,
	getConsentListing,
	getCurrentStep,
	getFormData,
	hideAllSteps,
	initRecaptchaDefaultQuad,
	rerunConsentSearchesOnInputChange,
	showStepConditionally,
	skipSteps,
	trackFormStepChangeDataLayer,
	trackFormStepGA,
	trackFormSubmissionFlow,
	updateTabIndexes,
	skipHiddenSteps
} from '../../../helpers';
import { isCurrentStepLast, isCurrentStepSecondToLast } from '../../../validators';

/**
 * Show step DOM element with specified ID.
 * @param {String} stepId - ID of DOM element for the step. Example: "step-1"
 */
export async function showStep(stepId) {
	const oldStepId = getCurrentStep().getAttribute('id');

	// Fire callback before switching steps
	if (modForm.opts.stepChangingCallback && 'function' === typeof modForm.opts.stepChangingCallback) {
		stepId = await modForm.opts.stepChangingCallback(oldStepId, stepId);
	}

	if (modForm.opts.useGlobalSkipSteps) {
		stepId = skipSteps(oldStepId, stepId, modForm.opts.skipStepsConfig);
	}

	if (modForm.opts.useShowStepConditionally) {
		stepId = showStepConditionally(oldStepId, stepId);
	}

	stepId = skipHiddenSteps(oldStepId, stepId);

	if (!stepId) {
		return;
	}

	// Track step switching in GA
	trackFormStepGA(stepId);

	const $currentStep = modForm.opts.form.querySelector('#' + stepId);

	// Retrieve the name of the current step and append it to the body as a data attribute
	const stepName = $currentStep.dataset.stepName ?? '';
	if (stepName) {
		document.body.dataset.activeStepName = stepName;
	}

	// Hide all steps and show specified
	hideAllSteps();
	$currentStep.classList.add(modForm.opts.stepActiveCls);
	if (modForm.opts.addAccessibilityUpdates && !modForm.opts.useGlobalInitCarousel) {
		$currentStep.setAttribute('aria-hidden', 'false');
	}

	if (modForm.opts.isVite && modForm.opts.scrollTopOnStepChange) {
		window.scrollTo({
			top: 0,
			behavior: 'smooth'
		});
	}

	const activeStepIndex = parseInt(stepId.slice(4));
	if (modForm.opts.addAccessibilityUpdates && modForm.opts.formSwiperInstance) {
		const swiperForm = modForm.opts.formSwiperInstance.el;
		if (activeStepIndex > 1) {
			swiperForm.setAttribute('tabindex', '-1');
			if (modForm.opts.toggleFormInProgressClass) {
				document.body.classList.add(modForm.opts.formInProgressCls);
			}
		} else {
			swiperForm.setAttribute('tabindex', '0');
			if (modForm.opts.toggleFormInProgressClass) {
				document.body.classList.remove(modForm.opts.formInProgressCls);
			}
		}
	}

	if (modForm.opts.showRecaptchaForDirectTraffic) {
		if (isCurrentStepSecondToLast()) {
			if (modForm.opts.quadLinkData.defaultQuad !== '') {
				modForm.opts.usingDefaultQuad = true;
				initRecaptchaDefaultQuad(activeStepIndex);
			}
		} else if (isCurrentStepLast()) {
			// Fallback if somehow they don't initialize recaptcha on the #personalInfo step
			if (modForm.opts.quadLinkData.defaultQuad !== '' && (!modForm.opts.recaptchaContainer || modForm.opts.recaptchaContainer.children.length === 0)) {
				modForm.opts.usingDefaultQuad = true;
				initRecaptchaDefaultQuad(activeStepIndex);
			}
		}
	}

	// Fire callback after switching steps
	if (modForm.opts.stepChangedCallback && 'function' === typeof modForm.opts.stepChangedCallback) {
		await modForm.opts.stepChangedCallback(oldStepId, stepId);
	}

	if (modForm.opts.vertical && modForm.opts.fetchAndFormatConsentListings && (activeStepIndex === modForm.opts.steps.length - 1)) {
		getConsentListing(modForm.opts.getConsentListingCallback);
	}

	if (modForm.opts.fetchAndFormatConsentListings && modForm.opts.rerunConsentSearchesOnInputChange && isCurrentStepSecondToLast()) {
		rerunConsentSearchesOnInputChange();
	}

	if (modForm.opts.gettingLiveConsent && isCurrentStepLast() && !modForm.opts.consentRequestId) {
		Modalytics.heap('track', ['Consent Searches | Not Available on Last Step']);
	}

	if (window.innerWidth < 768) {
		updateTabIndexes();
	}

	// track steps in dataLayer
	trackFormStepChangeDataLayer();

	const formData = getFormData();
	if ((formData && formData.xxTrustedFormToken) && !modForm.opts.isTfTokenStampedInHeap) {
		trackFormSubmissionFlow({ tfToken: formData.xxTrustedFormToken });
		modForm.opts.isTfTokenStampedInHeap = true;
	}

	addServiceCodeToHeap();
	// Fire custom page view
	Modalytics.heap('track', ['Page Visited']);
}
